import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { config } from './config/environment';
import HomePage from './pages/HomePage';
import SignUpPage from './pages/SignUpPage';
import LoginPage from './pages/LoginPage';
import RoleActivationPage from './pages/RoleActivationPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PleaseVerifyEmailPage from './pages/PleaseVerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import JoinUsPage from './pages/JoinUsPage';
import ParentDashboard from './pages/ParentDashboard';
import ParentChildDashboard from './pages/ParentChildDashboard';
import CourseDetailView from './pages/CourseDetailView';
import ParentCourses from './pages/ParentCourses';
import ParentProgressReports from './pages/ParentProgressReports';
import ParentAchievements from './pages/ParentAchievements';
import ParentMessages from './pages/ParentMessages';
import ParentMessageThread from './pages/ParentMessageThread';
import ParentComposeMessage from './pages/ParentComposeMessage';
import ParentMeetingScheduler from './pages/ParentMeetingScheduler';
import TeacherMessages from './pages/TeacherMessages';
import TeacherMessageThread from './pages/TeacherMessageThread';
import ParentProfile from './pages/ParentProfile';
import ParentSettings from './pages/ParentSettings';
import ParentCourseRegistrationPage from './pages/ParentCourseRegistrationPage';
import StudentDashboard from './pages/StudentDashboard';
import StudentDashboardV1 from './pages/StudentDashboardV1';
import StudentDashboardV2 from './pages/StudentDashboardV2';
import StudentDashboardV3 from './pages/StudentDashboardV3';

import StudentCourses from './pages/StudentCourses';
import BrowseAvailableCoursesPage from './pages/BrowseAvailableCoursesPage';
import StudentDeadlines from './pages/StudentDeadlines';
import StudentTacticalOverview from './pages/StudentTacticalOverview';
import StudentAccolades from './pages/StudentAccolades';
import StudentInsights from './pages/StudentInsights';
import StudentActivities from './pages/StudentActivities';
import CourseDetails from './pages/CourseDetails';
import SubjectDetails from './pages/SubjectDetails';
import TopicDetails from './pages/TopicDetails';
import Assignments from './pages/Assignments';
import AbacusCoursePage from './pages/AbacusCoursePage';
import AbacusPwaEntry from './pages/AbacusPwaEntry';
import AbacusTopicPage from './pages/AbacusTopicPage';
import TutorAbacusTopicPage from './pages/TutorAbacusTopicPage';
import AbacusTutorJoinPage from './pages/AbacusTutorJoinPage';

import CourseTypeSelection from './pages/CourseTypeSelection';
import { GuestProvider } from './context/GuestContext';
import GuestEntryPage from './pages/GuestEntryPage';
import GuestAvailableCoursesPage from './pages/GuestAvailableCoursesPage';
import GuestByokPage from './pages/GuestByokPage';
import GuestCourseView from './pages/GuestCourseView';
import GuestSearchPage from './pages/GuestSearchPage';
import GuestByLocationPage from './pages/GuestByLocationPage';
import GuestTopicView from './pages/GuestTopicView';
import SelfRegistration from './pages/SelfRegistration';
import CourseCodeRegistrationPage from './pages/CourseCodeRegistrationPage';
import TutorDashboard from './pages/TutorDashboard';
import TutorCourseManagement from './pages/TutorCourseManagement';
import TutorArchivedCoursesPage from './pages/TutorArchivedCoursesPage';
import TutorCourseStructure from './pages/TutorCourseStructure';
import TutorCourseCodes from './pages/TutorCourseCodes';
import TutorCourseRosterPage from './pages/TutorCourseRosterPage';
import TutorScheduledTestsPage from './pages/TutorScheduledTestsPage';
import TutorTestResultsPage from './pages/TutorTestResultsPage';
import StudentTestAttemptPage from './pages/StudentTestAttemptPage';
import StudentAttemptHistoryPage from './pages/StudentAttemptHistoryPage';
import TutorTopicContent from './pages/TutorTopicContent';
import TutorCourseQuizConfigPage from './pages/TutorCourseQuizConfigPage';
import TutorQuizReviewPage from './pages/TutorQuizReviewPage';
import TutorContentReviewPage from './pages/TutorContentReviewPage';
import TutorTopicAssessmentPage from './pages/TutorTopicAssessmentPage';
import TutorCourseDashboard from './pages/TutorCourseDashboard';
import TutorSessionWorkspacePage from './pages/TutorSessionWorkspacePage';
import TutorTopicSchedulePage from './pages/TutorTopicSchedulePage';
import StudentCourseNodePage from './pages/StudentCourseNodePage';
import CoursePlaygroundPage from './pages/CoursePlaygroundPage';
import SystemAdminDashboard from './pages/SystemAdminDashboard';
import SystemAdminManagement from './pages/SystemAdminManagement';
import UserManagement from './pages/UserManagement';
import AdminCourseManagement from './pages/AdminCourseManagement';
import AdminMcpPage from './pages/AdminMcpPage';
import AdminGuestAnalyticsPage from './pages/AdminGuestAnalyticsPage';

import SubjectTopicManagement from './pages/SubjectTopicManagement';
import SubjectTopicsPage from './pages/SubjectTopicsPage';
import AdminTopicDashboard from './pages/AdminTopicDashboard';
import LocationManagement from './pages/LocationManagement';
import SchoolManagement from './pages/SchoolManagement';
import SystemAdminLoginPage from './pages/SystemAdminLoginPage';
import GuestEligibilityAdminPage from './pages/GuestEligibilityAdminPage';
import CourseTypesAdminPage from './pages/CourseTypesAdminPage';
import CourseDashboard from './pages/CourseDashboard';
import ActivityTimeline from './pages/ActivityTimeline';
import ContentManagement from './pages/ContentManagement';
import StudentQuizHistoryPage from './pages/StudentQuizHistoryPage';
import CurriculumManagement from './pages/CurriculumManagement';
import CoursePreviewWrapper from './pages/preview/CoursePreviewWrapper';
import SubjectPreviewWrapper from './pages/preview/SubjectPreviewWrapper';
import TopicPreviewWrapper from './pages/preview/TopicPreviewWrapper';
import QuizPreviewWrapper from './pages/preview/QuizPreviewWrapper';

import AboutUs from './pages/static/AboutUs';
import ContactPage from './pages/static/ContactPage';
import HowItWorks from './pages/static/HowItWorks';
import CurriculumGuide from './pages/static/CurriculumGuide';
import SupportCenter from './pages/static/SupportCenter';
import PrivacyPolicy from './pages/static/PrivacyPolicy';
import TermsOfService from './pages/static/TermsOfService';
import CookiePolicy from './pages/static/CookiePolicy';
import ChangelogPage from './pages/ChangelogPage';
import TutorAbacusSessions from './pages/TutorAbacusSessions';
import UserProfileSettingsPage from './pages/UserProfileSettingsPage';

import Header from './components/Header';
import Footer from './components/Footer';
import { AbacusFocusProvider, useAbacusFocus } from './context/AbacusFocusContext';
import ProtectedRoute from './components/ProtectedRoute';

// StudyStride Brand Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2563EB', // StudyStride Blue
      light: '#3B82F6',
      dark: '#1D4ED8',
    },
    secondary: {
      main: '#0D9488', // StudyStride Teal
      light: '#14B8A6',
      dark: '#0F766E',
    },
    success: {
      main: '#16A34A',
    },
    background: {
      default: '#F8FAFC',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function AppShell({ children }: { children: React.ReactNode }) {
  const { focusActive } = useAbacusFocus();
  return (
    <div className="App">
      {!focusActive && <Header />}
      <main>{children}</main>
      {!focusActive && <Footer />}
    </div>
  );
}

function App() {
  const googleClientId = config.services.googleClientId;
  const content = (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Analytics />
      <SpeedInsights />
      <Router>
        <GuestProvider>
        <AbacusFocusProvider>
        <AppShell>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/role-activation" element={<RoleActivationPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/please-verify-email" element={<PleaseVerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/admin/login" element={<SystemAdminLoginPage />} />
              <Route path="/join-us" element={<JoinUsPage />} />

              {/* Guest (Try as Guest) – public, no auth */}
              <Route path="/guest" element={<GuestEntryPage />} />
              <Route path="/guest/available" element={<GuestAvailableCoursesPage />} />
              <Route path="/guest/byok" element={<GuestByokPage />} />
              <Route path="/guest/search" element={<GuestSearchPage />} />
              <Route path="/guest/by-location" element={<GuestByLocationPage />} />
              <Route path="/guest/topic/:topicId" element={<GuestTopicView />} />
              <Route path="/guest/course/:courseId" element={<GuestCourseView />} />

              {/* Static Pages */}
              <Route path="/about" element={<AboutUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/curriculum-guide" element={<CurriculumGuide />} />
              <Route path="/support-center" element={<SupportCenter />} />
              <Route path="/student-dashboard/v2" element={<ProtectedRoute><StudentDashboardV2 /></ProtectedRoute>} />
              <Route path="/student-dashboard/v3" element={<ProtectedRoute><StudentDashboardV3 /></ProtectedRoute>} />
              <Route
                path="/browse-courses"
                element={
                  <ProtectedRoute requiredUserTypes={['STUDENT', 'PARENT']}>
                    <BrowseAvailableCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/student-courses" element={<ProtectedRoute><StudentCourses /></ProtectedRoute>} />

              {/* Expanded Dashboard Routes */}
              <Route path="/student-deadlines" element={<ProtectedRoute><StudentDeadlines /></ProtectedRoute>} />
              <Route path="/student-tactical" element={<ProtectedRoute><StudentTacticalOverview /></ProtectedRoute>} />
              <Route path="/student-accolades" element={<ProtectedRoute><StudentAccolades /></ProtectedRoute>} />
              <Route path="/student-insights" element={<ProtectedRoute><StudentInsights /></ProtectedRoute>} />
              <Route path="/student-activities" element={<ProtectedRoute><StudentActivities /></ProtectedRoute>} />

              <Route path="/student-courses/:courseId" element={<ProtectedRoute><CourseDetails /></ProtectedRoute>} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/changelog" element={<ChangelogPage />} />

              {/* Protected routes - Parent only */}
              <Route
                path="/parent-dashboard"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-child-dashboard/:childId"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentChildDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-child-dashboard/:childId/course/:courseId"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <CourseDetailView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-courses"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentCourses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-progress-reports"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentProgressReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-achievements"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentAchievements />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-messages"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentMessages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent/messages/thread/:threadId"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentMessageThread />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent/messages/compose"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentComposeMessage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent/meetings/schedule"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentMeetingScheduler />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-profile"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-settings"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parent-register-child/:childId"
                element={
                  <ProtectedRoute requiredUserType="PARENT">
                    <ParentCourseRegistrationPage />
                  </ProtectedRoute>
                }
              />

              {/* Profile Settings — accessible to any authenticated user */}
              <Route
                path="/profile-settings"
                element={
                  <ProtectedRoute>
                    <UserProfileSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Tutor only */}
              <Route
                path="/tutor-dashboard"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/archived"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorArchivedCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/session"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorSessionWorkspacePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/schedule"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorTopicSchedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/structure"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseStructure />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/roster"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseRosterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/tests"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorScheduledTestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/tests/:testId/results"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorTestResultsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/codes"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseCodes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/nodes/:nodeId/content"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorTopicContent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/quiz-config"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorCourseQuizConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/quiz-review"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorQuizReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/courses/:courseId/content-review"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorContentReviewPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tutor/courses/:courseId/topics/:nodeId/assessment"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorTopicAssessmentPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Teacher only */}
              <Route
                path="/teacher/messages"
                element={
                  <ProtectedRoute requiredUserType="TEACHER">
                    <TeacherMessages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/messages/thread/:threadId"
                element={
                  <ProtectedRoute requiredUserType="TEACHER">
                    <TeacherMessageThread />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/courses/:courseId/playground"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <CoursePlaygroundPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/courses/:courseId/nodes/:nodeId"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentCourseNodePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/courses/:courseId/tests/:testId/attempt"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentTestAttemptPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/courses/:courseId/tests/:testId/attempts"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentAttemptHistoryPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - Student only */}
              <Route
                path="/student-dashboard"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-dashboard/v1"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentDashboardV1 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-dashboard/v2"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentDashboardV2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-dashboard/v3"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentDashboardV3 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentCourses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <CourseDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/abacus"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <AbacusPwaEntry />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/abacus"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <AbacusCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/abacus/topics/:topicId"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <AbacusTopicPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/abacus/tutor-sessions/:sessionId"
                element={
                  <ProtectedRoute requiredUserTypes={['STUDENT', 'PARENT', 'TUTOR', 'SYSTEM_ADMIN']}>
                    <AbacusTutorJoinPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/quiz-history"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <StudentQuizHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/subjects/:subjectId"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <SubjectDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/subjects/:subjectId/topics/:topicId"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <TopicDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student-courses/:courseId/subjects/:subjectId/assignments"
                element={
                  <ProtectedRoute requiredUserType="STUDENT">
                    <Assignments />
                  </ProtectedRoute>
                }
              />

              {/* Course Registration routes - Available for both Student and Parent */}
              <Route
                path="/register-course"
                element={
                  <ProtectedRoute requiredUserTypes={['STUDENT', 'PARENT']}>
                    <CourseTypeSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-course-self"
                element={
                  <ProtectedRoute requiredUserTypes={['STUDENT', 'PARENT']}>
                    <SelfRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-course-code"
                element={
                  <ProtectedRoute requiredUserTypes={['STUDENT', 'PARENT']}>
                    <CourseCodeRegistrationPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - System Admin only */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SystemAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/system-admins"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SystemAdminManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/courses"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <AdminCourseManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/course-types"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <CourseTypesAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/course-types/guest-eligibility"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <GuestEligibilityAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subjects"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SubjectTopicManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subjects/:subjectId/topics"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SubjectTopicsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subjects/:subjectId/topics/:topicId/dashboard"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <AdminTopicDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/courses/:courseId/content"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <ContentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/courses/:courseId/dashboard"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <CourseDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/preview/courses/:courseId"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <CoursePreviewWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/preview/courses/:courseId/subjects/:subjectId"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SubjectPreviewWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/preview/courses/:courseId/subjects/:subjectId/topics/:topicId"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <TopicPreviewWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/preview/courses/:courseId/subjects/:subjectId/quiz"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <QuizPreviewWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/courses/:courseId/dashboard/activity-timeline"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <ActivityTimeline />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/locations"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <LocationManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/schools"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <SchoolManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/curricula"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <CurriculumManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/curricula/:curriculumId/structure"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <CurriculumManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mcp"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <AdminMcpPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/guest-analytics"
                element={
                  <ProtectedRoute requiredUserType="SYSTEM_ADMIN">
                    <AdminGuestAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/abacus-sessions"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorAbacusSessions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tutor/abacus-sessions/:sessionId/topics/:topicId"
                element={
                  <ProtectedRoute requiredUserType="TUTOR">
                    <TutorAbacusTopicPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
        </AppShell>
        </AbacusFocusProvider>
        </GuestProvider>
      </Router>
    </ThemeProvider>
  );

  return googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>{content}</GoogleOAuthProvider>
  ) : (
    content
  );
}

export default App;
